import { useLocalization } from "@fluent/react";
import { Checkbox } from "@narmi/design_system";
import { ContextForm, ProgressButtons, useFormData } from "cerulean";
import ApiHttp from "byzantine/src/ApiHttp";
import { useCurrentUser } from "../contexts/CurrentUserContext";

type SubmitPreferencesProps = {
  institution: API.Institution;
  mustAddOrReverifyDeviceOnSubmit?: boolean;
};

const SubmitPreferences = ({
  institution,
  mustAddOrReverifyDeviceOnSubmit
}: SubmitPreferencesProps) => {
  const { show_checkbox_on_enrollment_preferences_page: showCheckbox } =
    institution;
  const { l10n } = useLocalization();
  const { formData, onChange } = useFormData<{ reviewTermsPrompt?: boolean }>(
    {}
  );
  const { currentUser } = useCurrentUser();

  const onFinish = async (callback: (errors?: unknown) => void) => {
    // notify the user that they must add/reverify a device using via the Two-factor authentication component
    // iff mfa is required, email is not a permitted device, and they have not added/verified a device as of the current page load
    if (mustAddOrReverifyDeviceOnSubmit) {
      callback(l10n.getString("error-add-mfa-device"));
      return;
    }
    /*
    makes a call to set is_converting false, effectively concluding the enrollment/conversion process
    will fail if mfa is required and the user doesn't have a permitted device (or backup codes), or if the user is already done converting
    note that if the user is done converting, we would already redirect them into OLB anyway
    */
    currentUser
      ?.sendEnrollmentCompletedRequest()
      .then(() => {
        // redirect the user to the dashboard upon successful request
        window.location.href = "/dashboard";
      })
      /*
      we do a lot of client-side validation before making the above request,
      but in the event of an error, just present it to the user
      */
      .catch((err) => callback(err));
  };

  return (
    <ContextForm data={formData} onChange={onChange}>
      {showCheckbox && (
        <ContextForm.Field
          className="margin--top--m"
          validate={(value) => {
            if (!value) {
              return l10n.getString("error-accept-preferences");
            }
            return "";
          }}
        >
          <Checkbox
            {...{ field: "reviewTermsPrompt" }}
            label={l10n.getString("label-accept-preferences")}
          />
        </ContextForm.Field>
      )}
      <div className="progress-buttons-container">
        <ProgressButtons
          onNext={onFinish}
          nextLabel={l10n.getString("button-finish")}
          onBack={() => {
            if (!institution?.logout_redirect) {
              ApiHttp.fetch("logout", { endpointIsUrl: true }).then(() => {
                window.location.assign("/enroll");
              });
            } else {
              window.open("/logout", "_self");
            }
          }}
          backLabel={l10n.getString("button-cancel")}
        />
      </div>
    </ContextForm>
  );
};

export default SubmitPreferences;
